import React from 'react';
import { projectService } from '@ideoz/core';
import { toast } from 'sonner';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '../atoms/drawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../atoms/dialog';
import { Button } from '../atoms/button';
import { Label } from '../atoms/label';
import ColorPicker from '../molecules/ColorPicker';
import Icon from '../molecules/Icon';
import { ArrowLeft, Check, ChevronLeft } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../atoms/select';

type DesignSystem = {
  id?: string;
  projectId?: string;
  primaryColor?: string;
  primaryForeground?: string;
  secondaryColor?: string;
  secondaryForeground?: string;
  fontFamily?: string;
  theme?: 'light' | 'dark';
};

type Logo = {
  id?: string;
  projectId?: string;
  fileName?: string;
  file?: File;
  mimeType?: string;
  fileSize?: number;
};

export type DesignSystemDrawerProps = {
  open: boolean;
  onClose: () => void;
  projectId?: string | null;
  onUpdated?: (payload: { designSystem?: DesignSystem; logo?: Logo } | null) => void;
};

const defaultFonts = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Raleway',
  'Nunito',
  'Work Sans',
  'Playfair Display',
  'Source Sans Pro',
  'Ubuntu',
  'PT Sans',
  'Merriweather',
  'Oswald',
  'Rubik',
  'DM Sans',
  'Space Grotesk',
  'Plus Jakarta Sans',
  'Manrope',
  'Outfit',
  'IBM Plex Sans',
  'Noto Sans',
  'Exo 2',
  'Quicksand',
  'Mulish',
  'Karla',
  'Barlow',
  'Fira Sans',
  'Archivo',
  'Lexend',
  'Red Hat Display',
  'Epilogue',
  'Sora',
  'Instrument Sans',
  'Geist',
  'Geist Mono',
];

// Default design system values
const DEFAULT_DESIGN_SYSTEM: DesignSystem = {
  primaryColor: '#3B82F6',
  primaryForeground: '#FFFFFF',
  secondaryColor: '#8B5CF6',
  secondaryForeground: '#FFFFFF',
  fontFamily: 'Exo 2',
  theme: 'light',
};

const DesignSystemDrawer: React.FC<DesignSystemDrawerProps> = ({
  open,
  onClose,
  projectId,
  onUpdated,
}) => {
  const [saving, setSaving] = React.useState(false);
  const [designSystem, setDesignSystem] = React.useState<DesignSystem>({
    ...DEFAULT_DESIGN_SYSTEM,
  });
  const [logo, setLogo] = React.useState<Logo | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = React.useState(false);
  const [initialHasLogo, setInitialHasLogo] = React.useState(false);
  const [removingLogo, setRemovingLogo] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [hasUpdatedDesignSystem, setHasUpdatedDesignSystem] = React.useState(false);
  const [showResetDialog, setShowResetDialog] = React.useState(false);

  React.useEffect(() => {
    if (!open || !projectId) return;
    (async () => {
      try {
        const data = await projectService.getDesignSystem(projectId);
        // API may return array with designSystem and logo
        const item = Array.isArray(data) && data.length > 0 ? data[0] : data;

        if (item?.designSystem) {
          const fetchedDesignSystem = item.designSystem as DesignSystem;
          setDesignSystem(fetchedDesignSystem);

          // Check if the fetched design system is different from defaults
          const isCustomized =
            fetchedDesignSystem.primaryColor !== DEFAULT_DESIGN_SYSTEM.primaryColor ||
            fetchedDesignSystem.primaryForeground !== DEFAULT_DESIGN_SYSTEM.primaryForeground ||
            fetchedDesignSystem.secondaryColor !== DEFAULT_DESIGN_SYSTEM.secondaryColor ||
            fetchedDesignSystem.secondaryForeground !== DEFAULT_DESIGN_SYSTEM.secondaryForeground ||
            fetchedDesignSystem.fontFamily !== DEFAULT_DESIGN_SYSTEM.fontFamily ||
            fetchedDesignSystem.theme !== DEFAULT_DESIGN_SYSTEM.theme;

          setHasUpdatedDesignSystem(isCustomized);
        } else {
          // No design system found, use defaults
          setDesignSystem({ ...DEFAULT_DESIGN_SYSTEM });
          setHasUpdatedDesignSystem(false);
        }

        if (item?.logo) {
          const serverLogo = item.logo as any;
          setInitialHasLogo(true);
          if (serverLogo.fileContent) {
            const mime = serverLogo.mimeType || 'image/png';
            setPreviewUrl(`data:${mime};base64,${serverLogo.fileContent}`);
            setLogo({
              id: serverLogo.id,
              projectId: serverLogo.projectId,
              fileName: serverLogo.fileName,
              mimeType: serverLogo.mimeType,
              fileSize: serverLogo.fileSize,
            });
          } else if (typeof serverLogo === 'string') {
            setPreviewUrl(serverLogo);
            setLogo({ fileName: undefined } as any);
          } else if (serverLogo.url || serverLogo.fileUrl) {
            setPreviewUrl(serverLogo.url || serverLogo.fileUrl);
            setLogo({ fileName: serverLogo.fileName } as any);
          } else {
            setLogo(serverLogo as Logo);
          }
          setLogoRemoved(false);
          // Having a logo also means design system was customized
          setHasUpdatedDesignSystem(true);
        } else {
          setInitialHasLogo(false);
        }
      } catch (err) {
        // keep defaults on error
        console.error(err);
        setDesignSystem({ ...DEFAULT_DESIGN_SYSTEM });
        setHasUpdatedDesignSystem(false);
      }
    })();
  }, [open, projectId]);

  React.useEffect(() => {
    // when a local File is selected in `logo.file`, create an object URL for preview
    if (logo?.file) {
      const url = URL.createObjectURL(logo.file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return;
  }, [logo]);

  const handleFileChange = (file?: File | null) => {
    // selecting a file clears any prior "removed" intent
    setLogoRemoved(false);
    if (!file) {
      setLogo(null);
      setPreviewUrl(null);
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          Invalid file type. Only JPG, PNG, and SVG files are allowed.
        </span>,
        {
          style: {
            padding: '8px 12px',
            width: 'fit-content',
            maxWidth: 'fit-content',
            marginLeft: '-12px',
          },
        }
      );
      return;
    }

    // Validate file size (4MB = 4 * 1024 * 1024 bytes)
    const maxSize = 4 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size exceeds 4 MB. Please upload a smaller file.');
      return;
    }

    // create object URL for immediate preview
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setLogo({ fileName: file.name, file: file, mimeType: file.type, fileSize: file.size });
  };

  const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFileChange(f);
    // reset so same file can be chosen again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadButtonClick = () => fileInputRef.current?.click();

  const handleRemove = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    // Clearing the logo from the preview is a client-side action only.
    // The actual server-side removal will be performed when the user clicks "Update" (Save).
    setLogo(null);
    setPreviewUrl(null);
    setLogoRemoved(true);
    // Keep initialHasLogo true — it indicates the server had a logo when the drawer opened.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileChange(f);
  };

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      // Update design system via PATCH (API expects plain object with color/font fields)
      // Do not include projectId or other server-managed fields in the payload
      const sanitizedDesignPayload = {
        primaryColor: designSystem.primaryColor,
        primaryForeground: designSystem.primaryForeground,
        secondaryColor: designSystem.secondaryColor,
        secondaryForeground: designSystem.secondaryForeground,
        fontFamily: designSystem.fontFamily,
        theme: designSystem.theme,
      };

      // We don't include logo: null inside the design system PATCH payload.
      // Logo removal is performed via the DELETE endpoint during Save (below),
      // and the refreshed payload from getDesignSystem will contain `logo: null`.

      // Update design system (colors, font)
      try {
        await projectService.updateDesignSystem(projectId, sanitizedDesignPayload);
      } catch (err: any) {
        const msg = err?.message || 'Failed to update design system';
        toast.error(msg);
        return;
      }

      // If the user removed a server-stored logo (logoRemoved && initialHasLogo),
      // delete it now on the server.
      if (logoRemoved && initialHasLogo) {
        try {
          await projectService.deleteDesignSystemLogo(projectId);
          // mark it as cleared so we don't try to delete again
          setInitialHasLogo(false);
        } catch (err: any) {
          const msg = err?.message || 'Failed to remove logo';
          toast.error(msg);
          return;
        }
      }

      // If a file was selected, upload it to the design-system logo endpoint
      if (logo?.file) {
        try {
          const formData = new FormData();
          formData.append('logo', logo.file);
          await projectService.uploadDesignSystemLogo(projectId, formData);
        } catch (err: any) {
          const msg = err?.message || 'Failed to upload logo';
          toast.error(msg);
          return;
        }
      }

      // Refresh design system from server and notify parent
      try {
        const refreshed = await projectService.getDesignSystem(projectId);
        const item = Array.isArray(refreshed) && refreshed.length > 0 ? refreshed[0] : refreshed;
        onUpdated?.(item || null);
      } catch (refreshErr) {
        // ignore refresh errors — main operation succeeded
      }

      // Mark as updated after successful save
      setHasUpdatedDesignSystem(true);

      toast.success('Design system has been updated successfully');
      onClose();
    } catch (err) {
      console.error(err);
      // show toast elsewhere or keep it simple for now
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Reset all values to defaults
    setDesignSystem({ ...DEFAULT_DESIGN_SYSTEM });
    setLogo(null);
    setPreviewUrl(null);
    setLogoRemoved(true);
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Update API with reset values
    (async () => {
      if (!projectId) return;
      setSaving(true);
      try {
        // Update design system to defaults
        await projectService.updateDesignSystem(projectId, { ...DEFAULT_DESIGN_SYSTEM });

        // Remove logo if present
        if (initialHasLogo) {
          try {
            await projectService.deleteDesignSystemLogo(projectId);
            setInitialHasLogo(false);
          } catch (err: any) {
            const msg = err?.message || 'Failed to remove logo';
            toast.error(msg);
          }
        }

        // Refresh and notify parent
        try {
          const refreshed = await projectService.getDesignSystem(projectId);
          const item = Array.isArray(refreshed) && refreshed.length > 0 ? refreshed[0] : refreshed;
          onUpdated?.(item || null);
        } catch {}

        toast.success('Design system values reset successfully');
        onClose();
      } catch (err) {
        console.error(err);
      } finally {
        setSaving(false);
        setShowResetDialog(false);
      }
    })();
    // Close the dialog immediately for UX
    setShowResetDialog(false);
    // Optionally: toast will be shown after API call
    // Drawer will close after API call
  };

  return (
    <>
      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Reset design system?
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-gray-600 leading-relaxed">
            Your design system keeps your prototype aligned with your brand colors, typography,
            spacing, and components. If you reset it, Ideoz will generate a generic UI without brand
            identity.
          </DialogDescription>
          <DialogFooter className="gap-3 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowResetDialog(false)}
              className="border-gray-300 text-gray-700"
            >
              Keep design system
            </Button>
            <Button
              onClick={handleReset}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Drawer open={open} onOpenChange={() => onClose()} direction="right">
        <DrawerContent className="mx-auto max-w-[520px]">
          <DrawerHeader className="px-5 py-5 border-gray-200">
            <div className="flex items-center justify-between full-width flex-1">
              <DrawerTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <DrawerClose asChild>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={onClose}
                      variant="ghost"
                      size="icon-default"
                      className="h-8 w-8 !gap-1 hover:bg-gray-100"
                    >
                      {/* Use Icon component instead of inline SVG */}
                      <Icon icon={ChevronLeft} size="22" strokeWidth={2} />
                    </Button>
                    <h2 className="text-lg font-medium text-gray-800">Brand Sync</h2>
                  </div>
                </DrawerClose>
              </DrawerTitle>
            </div>
          </DrawerHeader>

          <div className="px-4 space-y-6 overflow-auto">
            <div>
              <Label className="text-[#4B579F] text-sm font-semibold">Upload your logo</Label>
              <div
                onClick={() => fileInputRef.current?.click()} // أي مكان يفتح الـ file picker
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center mt-3 py-4 rounded-md border transition-colors ${
                  isDragOver
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-dashed border-[#E1E1E1] bg-gray-50'
                }`}
              >
                {previewUrl ? (
                  <div className="relative w-full flex items-center justify-center p-4 cursor-pointer">
                    <img src={previewUrl} alt="Logo preview" className="max-h-28 object-contain" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove();
                      }} // منع الفتح عند الضغط على زر الإزالة
                      aria-label="Remove logo"
                      disabled={removingLogo || saving}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100 disabled:opacity-50"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#111827"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 cursor-pointer">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                    >
                      <path
                        d="M5.69547 27.6372L14.4909 18.8418C15.0189 18.3138 15.2829 18.0498 15.5873 17.9509C15.8551 17.8639 16.1436 17.8639 16.4114 17.9509C16.7158 18.0498 16.9798 18.3138 17.5078 18.8418L26.2446 27.5786M18.666 20L22.4909 16.1752C23.0189 15.6471 23.2829 15.3831 23.5873 15.2842C23.8551 15.1972 24.1436 15.1972 24.4114 15.2842C24.7158 15.3831 24.9798 15.6471 25.5078 16.1752L29.3327 20M13.3327 12C13.3327 13.4728 12.1388 14.6667 10.666 14.6667C9.19326 14.6667 7.99935 13.4728 7.99935 12C7.99935 10.5272 9.19326 9.33333 10.666 9.33333C12.1388 9.33333 13.3327 10.5272 13.3327 12ZM9.06602 28H22.9327C25.1729 28 26.293 28 27.1486 27.564C27.9013 27.1805 28.5132 26.5686 28.8967 25.816C29.3327 24.9603 29.3327 23.8402 29.3327 21.6V10.4C29.3327 8.15979 29.3327 7.03969 28.8967 6.18404C28.5132 5.43139 27.9013 4.81947 27.1486 4.43597C26.293 4 25.1729 4 22.9327 4H9.06602C6.82581 4 5.7057 4 4.85005 4.43597C4.09741 4.81947 3.48548 5.43139 3.10199 6.18404C2.66602 7.03969 2.66602 8.15979 2.66602 10.4V21.6C2.66602 23.8402 2.66602 24.9603 3.10199 25.816C3.48548 26.5686 4.09741 27.1805 4.85005 27.564C5.7057 28 6.82581 28 9.06602 28Z"
                        stroke="#252B37"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex flex-col items-center">
                      {logo?.fileName ? (
                        <span className="flex text-[12px] text-[#333A63]">
                          Selected: {logo.fileName}
                        </span>
                      ) : (
                        <span className="flex text-[12px] text-[#333A63]">
                          Drag & drop or click to add your logo file
                        </span>
                      )}
                      <span className="flex text-[10px] text-gray-500">JPG, PNG, SVG file</span>
                      <span className="flex text-[10px] text-gray-700">
                        Max size: 4 MB per file
                      </span>
                    </div>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleHiddenInputChange}
                  className="hidden"
                />
              </div>
            </div>

            <h4 className="text-[#4B579F] text-sm font-semibold">Select your brand color system</h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-secondry-700 text-base text-medium">Primary</Label>
                <ColorPicker
                  label="Primary"
                  value={designSystem.primaryColor || '#3B82F6'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, primaryColor: v }))}
                />
              </div>
              <div>
                <Label className="text-secondry-700 text-base text-medium">
                  Primary foreground
                </Label>
                <ColorPicker
                  label="Primary/Foreground"
                  value={designSystem.primaryForeground || '#FFFFFF'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, primaryForeground: v }))}
                />
              </div>
              <div>
                <Label className="text-secondry-700 text-base text-medium">Secondary</Label>
                <ColorPicker
                  label="Secondary"
                  value={designSystem.secondaryColor || '#8B5CF6'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, secondaryColor: v }))}
                />
              </div>
              <div>
                <Label className="text-secondry-700 text-base text-medium">
                  Secondary foreground
                </Label>
                <ColorPicker
                  label="Secondary/Foreground"
                  value={designSystem.secondaryForeground || '#FFFFFF'}
                  onChange={(v) => setDesignSystem((s) => ({ ...s, secondaryForeground: v }))}
                />
              </div>
            </div>

            <div>
              <h4 className="text-[#4B579F] text-sm font-semibold">Define your typography</h4>
              <Label className="text-secondry-700 text-base text-medium">Select font</Label>
              <div className="mt-2">
                <Select
                  value={designSystem.fontFamily}
                  onValueChange={(v) => setDesignSystem((s) => ({ ...s, fontFamily: v }))}
                >
                  <SelectTrigger className="w-full justify-between">
                    <SelectValue placeholder={designSystem.fontFamily} />
                  </SelectTrigger>

                  <SelectContent>
                    {defaultFonts.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Prototype Theme Selection */}
            <div className="mt-6">
              <h4 className="text-[#4B579F] text-sm font-semibold mb-2">
                What is your Prototype Theme?
              </h4>
              <div className="flex gap-3">
                <div
                  className={`px-4 py-2 rounded-full border cursor-pointer text-sm font-medium transition-colors flex items-center gap-2
              ${designSystem.theme === 'light' || !designSystem.theme ? 'bg-[#ecfdf3] border-[#2af37c] text-[#347c5c]' : 'bg-white border-[#E1E1E1] text-gray-600'}`}
                  onClick={() => setDesignSystem((s) => ({ ...s, theme: 'light' }))}
                  aria-pressed={designSystem.theme === 'light' || !designSystem.theme}
                  tabIndex={0}
                >
                  {(designSystem.theme === 'light' || !designSystem.theme) && (
                    <Icon icon={Check} size={18} strokeWidth={2} color="#2af37c" />
                  )}
                  Light theme
                </div>
                <div
                  className={`px-4 py-2 rounded-full border cursor-pointer text-sm font-medium transition-colors flex items-center gap-2
              ${designSystem.theme === 'dark' ? 'bg-[#ecfdf3] border-[#2af37c] text-[#347c5c]' : 'bg-white border-[#E1E1E1] text-gray-600'}`}
                  onClick={() => setDesignSystem((s) => ({ ...s, theme: 'dark' }))}
                  aria-pressed={designSystem.theme === 'dark'}
                  tabIndex={0}
                >
                  {designSystem.theme === 'dark' && (
                    <Icon icon={Check} size={18} strokeWidth={2} color="#2af37c" />
                  )}
                  Dark theme
                </div>
              </div>
            </div>
          </div>

          <DrawerFooter>
            <div className="flex items-center justify-end gap-3 w-full">
              {hasUpdatedDesignSystem ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShowResetDialog(true)}
                    className="border-gray-300 text-gray-700 text-sm"
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="bg-[#1E2340] text-white text-sm"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Update'}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={onClose} className="border-0 text-sm">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="bg-[#1E2340] text-white text-sm"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Update'}
                  </Button>
                </>
              )}
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default DesignSystemDrawer;
